import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Bell, ChevronDown, Shield, Building, ShieldAlert, Radio, AlertTriangle, CheckCircle, X, Menu, Sun, Moon, Sparkles, Download } from 'lucide-react';
import { useAuth } from '../../auth/AuthProvider';
import { apiClient } from '../../api';
import useStore from '../../store/useStore';
import { useChat } from '../../contexts/ChatContext';
import { APP_DOWNLOAD_URL, LINUX_APPIMAGE_URL, isLinuxClient, useDesktopApp, useLinuxDesktopApp } from '../../config/desktopApp';

const NOTIF_TYPES = {
  ioc:      { icon: AlertTriangle, color: 'text-red-400',     bg: 'bg-red-400/10',     border: 'border-red-400/20',     label: 'IoC Threat',     route: '/threats' },
  cve:      { icon: ShieldAlert,   color: 'text-amber-400',   bg: 'bg-amber-400/10',   border: 'border-amber-400/20',   label: 'CVE Alert',      route: '/vulnerabilities' },
  asset:    { icon: Radio,         color: 'text-orange-400',   bg: 'bg-orange-500/10',   border: 'border-orange-500/20',  label: 'New Asset',      route: '/network-map' },
};

function timeAgo(isoString) {
  if (!isoString) return '';
  const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const Header = () => {
  const { user, logout } = useAuth();
  const { setSidebarMinimized, theme, toggleTheme } = useStore();
  const navigate = useNavigate();
  const { open: assistantOpen, setOpen: setAssistantOpen } = useChat();
  const { available: windowsAppAvailable } = useDesktopApp();
  const { available: linuxAppAvailable } = useLinuxDesktopApp();
  // Offer the build that matches this computer.
  const onLinux = isLinuxClient();
  const appAvailable = onLinux ? linuxAppAvailable : windowsAppAvailable;
  const appUrl = onLinux ? LINUX_APPIMAGE_URL : APP_DOWNLOAD_URL;
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [seenIds, setSeenIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('seen_notif_ids') || '[]')); }
    catch { return new Set(); }
  });
  const notifRef = useRef(null);
  const userRef = useRef(null);
  const dark = theme !== 'light';

  const fetchNotifications = useCallback(async () => {
    try {
      const [iocRes, cveRes, serversRes] = await Promise.allSettled([
        apiClient.get('/api/alerts/ioc?resolved=0'),
        apiClient.get('/api/alerts/cve?resolved=0'),
        apiClient.get('/api/servers'),
      ]);

      const items = [];

      if (iocRes.status === 'fulfilled') {
        const alerts = iocRes.value.data.ioc_alerts || [];
        alerts.slice(0, 5).forEach(a => {
          const isOffline = a.ioc_type === 'agent_offline';
          items.push({
          id: `ioc-${a.id}`,
          type: 'ioc',
          title: isOffline ? 'Agent Offline' : `IoC ${(a.ioc_type || '').toUpperCase()} matched`,
          detail: isOffline ? `${a.ioc_value} — not reporting` : (a.ioc_value || ''),
          time: a.detected_at,
          severity: a.threat_level,
        });
        });
      }

      if (cveRes.status === 'fulfilled') {
        const alerts = cveRes.value.data.cve_alerts || [];
        alerts.slice(0, 5).forEach(a => items.push({
          id: `cve-${a.id}`,
          type: 'cve',
          title: a.cve_id || 'CVE Alert',
          detail: `${a.package_name}@${a.version} — ${a.severity}`,
          time: a.detected_at,
          severity: a.severity,
        }));
      }

      if (serversRes.status === 'fulfilled') {
        const servers = serversRes.value.data.servers || [];
        const discovered = servers
          .filter(s => s.lifecycle_state === 'DISCOVERED')
          .sort((a, b) => (b.last_seen || '').localeCompare(a.last_seen || ''))
          .slice(0, 3);
        discovered.forEach(s => items.push({
          id: `asset-${s.id}`,
          type: 'asset',
          title: 'New device discovered',
          detail: `${s.hostname || s.ip_address} — ${s.ip_address}`,
          time: s.last_seen,
          severity: null,
        }));
      }

      // Sort by time descending
      items.sort((a, b) => (b.time || '').localeCompare(a.time || ''));

      setNotifications(items.slice(0, 10));
      const unseen = items.filter(n => !seenIds.has(n.id)).length;
      setUnreadCount(unseen);
    } catch {
      // Non-critical — notifications are informational only
    }
  }, [seenIds]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifications(false);
      if (userRef.current && !userRef.current.contains(e.target)) setShowUserMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpenNotifications = () => {
    setShowNotifications(v => !v);
    if (!showNotifications) {
      // Mark all as seen
      const ids = notifications.map(n => n.id);
      const next = new Set([...seenIds, ...ids]);
      setSeenIds(next);
      setUnreadCount(0);
      localStorage.setItem('seen_notif_ids', JSON.stringify([...next]));
    }
    setShowUserMenu(false);
  };

  const handleNotifClick = (notif) => {
    setShowNotifications(false);
    navigate(NOTIF_TYPES[notif.type]?.route || '/');
  };

  const SEVERITY_DOT = {
    high: 'bg-red-400', CRITICAL: 'bg-red-400', HIGH: 'bg-red-400',
    medium: 'bg-amber-400', MEDIUM: 'bg-amber-400',
    low: 'bg-emerald-400', LOW: 'bg-emerald-400',
  };

  return (
    <header
      className="h-16 backdrop-blur-sm border-b flex items-center justify-between px-4 sm:px-6 sticky top-0 z-50 gap-4"
      style={{ background: 'color-mix(in srgb, var(--surf-surface) 95%, transparent)', borderColor: 'var(--surf-line)' }}
    >
      {/* Left side: Logo + Hamburger + Search */}
      <div className="flex items-center flex-1 min-w-0 gap-4">
        <img src="/UMDE_logo.png" alt="UM-DragonEye" className="h-10 w-auto shrink-0" />

        <button onClick={setSidebarMinimized} className="md:hidden p-2 rounded-lg transition-colors shrink-0" style={{ color: 'var(--ink-muted)' }} aria-label="Toggle menu">
          <Menu className="w-5 h-5" />
        </button>

        <div
          title="Global search — coming soon"
          className="hidden lg:flex items-center gap-2.5 w-[360px] h-10 px-3 rounded-lg border shrink-0 opacity-60"
          style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}
        >
          <Search className="w-4 h-4 shrink-0" style={{ color: 'var(--ink-faint)' }} />
          <input
            type="search"
            disabled
            aria-label="Search (coming soon)"
            placeholder="Search assets, IPs, CVEs, cases"
            className="flex-grow min-w-0 bg-transparent border-0 outline-none text-sm cursor-not-allowed"
            style={{ color: 'var(--ink-text)' }}
          />
          <span className="font-mono text-[11px] px-1.5 py-0.5 border rounded shrink-0" style={{ color: 'var(--ink-faint)', borderColor: 'var(--surf-line)' }}>Ctrl K</span>
        </div>

        <button
          type="button"
          onClick={() => setAssistantOpen(o => !o)}
          aria-pressed={assistantOpen}
          aria-controls="assistant"
          className="h-10 flex items-center gap-[9px] pl-3 pr-2.5 rounded-[10px] border text-[13.5px] font-medium shrink-0"
          style={{ background: assistantOpen ? 'var(--surf-raised)' : 'transparent', borderColor: assistantOpen ? 'var(--accent)' : 'var(--surf-line-strong)', color: 'var(--ink-text)' }}
        >
          <Sparkles className="w-4 h-4" strokeWidth={1.9} style={{ color: 'var(--accent)' }} />
          <span className="hidden sm:inline">Ask DragonEye</span>
          <span className="hidden md:inline font-mono text-[10.5px] px-[5px] py-px border rounded-[5px]" style={{ color: 'var(--ink-faint)', borderColor: 'var(--surf-line)' }}>Ctrl J</span>
        </button>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-2 shrink-0">
        {/* Desktop app download: only offered once a release is published */}
        {appAvailable && <a
          href={appUrl}
          download
          className="relative hidden lg:flex items-center gap-2 h-10 px-3.5 rounded-[10px] border text-[13.5px] font-semibold"
          style={{ borderColor: 'var(--accent)', background: 'color-mix(in srgb, var(--accent) 10%, transparent)', color: 'var(--ink-link)' }}
        >
          <Download className="w-4 h-4" strokeWidth={1.9} />
          Download the app
          <span
            aria-hidden="true"
            className="absolute -top-2 -right-2 h-[18px] px-1.5 rounded-[9px] flex items-center font-mono text-[9.5px] tracking-[0.06em]"
            style={{ background: 'var(--accent)', color: '#0A0A0A' }}
          >
            NEW
          </span>
        </a>}

        {/* Theme toggle */}
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
          className="w-11 h-11 flex items-center justify-center rounded-lg border transition-colors"
          style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}
        >
          {dark ? <Sun className="w-[18px] h-[18px]" strokeWidth={1.8} /> : <Moon className="w-[18px] h-[18px]" strokeWidth={1.8} />}
        </button>

        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={handleOpenNotifications}
            aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
            className="relative w-11 h-11 flex items-center justify-center rounded-lg border transition-colors"
            style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}
          >
            <Bell className="w-[18px] h-[18px]" strokeWidth={1.8} />
            {unreadCount > 0 && (
              <span
                className="absolute top-1.5 right-1.5 min-w-[16px] h-4 rounded-full flex items-center justify-center font-mono text-[10px] font-medium px-1"
                style={{ background: 'var(--accent)', color: '#0A0A0A' }}
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {showNotifications && (
            <div
              className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-96 max-w-sm rounded-xl shadow-2xl border overflow-hidden z-50"
              style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--surf-line)' }}>
                <h3 className="text-sm font-bold" style={{ color: 'var(--ink-text)' }}>Notifications</h3>
                <button onClick={() => setShowNotifications(false)} style={{ color: 'var(--ink-faint)' }}>
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="max-h-[400px] overflow-y-auto divide-y" style={{ borderColor: 'var(--surf-line)' }}>
                {notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10" style={{ color: 'var(--ink-faint)' }}>
                    <CheckCircle className="w-8 h-8 mb-2" />
                    <p className="text-sm">All clear — no active alerts</p>
                  </div>
                ) : (
                  notifications.map(notif => {
                    const cfg = NOTIF_TYPES[notif.type] || NOTIF_TYPES.asset;
                    const Icon = cfg.icon;
                    const dot = SEVERITY_DOT[notif.severity] || 'bg-gray-500';
                    return (
                      <button
                        key={notif.id}
                        onClick={() => handleNotifClick(notif)}
                        className="w-full flex items-start gap-3 px-4 py-3 transition-colors text-left hover:opacity-80"
                      >
                        <div className={`mt-0.5 p-1.5 rounded-lg ${cfg.bg} flex-shrink-0`}>
                          <Icon className={`w-3.5 h-3.5 ${cfg.color}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dot}`} />
                            <span className={`text-xs font-semibold ${cfg.color}`}>{cfg.label}</span>
                          </div>
                          <p className="text-sm font-medium truncate mt-0.5" style={{ color: 'var(--ink-text)' }}>{notif.title}</p>
                          <p className="text-xs truncate" style={{ color: 'var(--ink-faint)' }}>{notif.detail}</p>
                        </div>
                        <span className="text-xs flex-shrink-0 mt-1" style={{ color: 'var(--ink-faint)' }}>{timeAgo(notif.time)}</span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        <span aria-hidden="true" className="w-px h-6 mx-1" style={{ background: 'var(--surf-line)' }} />

        {/* User menu */}
        <div className="relative" ref={userRef}>
          <button
            onClick={() => { setShowUserMenu(v => !v); setShowNotifications(false); }}
            className="flex items-center gap-2.5 h-11 pl-1 pr-2 rounded-lg transition-colors"
            style={{ color: 'var(--ink-text)' }}
          >
            <span
              className="w-[34px] h-[34px] rounded-full border flex items-center justify-center text-xs font-semibold shrink-0"
              style={{ background: 'var(--surf-raised)', borderColor: 'var(--surf-line)' }}
            >
              {(user?.name || user?.sub || 'U')[0].toUpperCase()}
            </span>
            <span className="hidden sm:flex flex-col items-start">
              <span className="text-[13px] font-medium">{user?.name || user?.sub?.split('@')[0] || 'Admin'}</span>
              <span className="font-mono text-[10.5px]" style={{ color: 'var(--ink-faint)' }}>
                {user?.role === 'admin' ? 'Org Admin' : (user?.role || 'Member')}
              </span>
            </span>
            {user?.role === 'admin' && (
              <span className="hidden sm:flex items-center gap-1 px-1.5 py-0.5 bg-purple-500/10 text-purple-400 text-xs font-semibold rounded border border-purple-500/20">
                <Shield className="w-2.5 h-2.5" /> SA
              </span>
            )}
            {user?.tenant_id && (
              <span
                className="hidden md:flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded border"
                style={{ background: 'var(--surf-raised)', color: 'var(--ink-muted)', borderColor: 'var(--surf-line)' }}
              >
                <Building className="w-2.5 h-2.5" />
                {user.tenant_id.substring(0, 8)}
              </span>
            )}
            <ChevronDown className="w-4 h-4" style={{ color: 'var(--ink-faint)' }} />
          </button>

          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-56 rounded-xl shadow-2xl border py-2 z-50" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
              <div className="px-4 py-2 border-b" style={{ borderColor: 'var(--surf-line)' }}>
                <p className="text-sm font-medium truncate" style={{ color: 'var(--ink-text)' }}>{user?.sub || user?.name}</p>
                <p className="text-xs" style={{ color: 'var(--ink-faint)' }}>{user?.role || 'No role'}</p>
              </div>
              <button
                onClick={logout}
                className="block w-full text-left px-4 py-2 text-sm text-red-400 transition-colors hover:opacity-80"
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
